import { useState, useRef } from 'react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Copy, RefreshCw, Download, QrCode } from 'lucide-react';
import { toast } from 'sonner';

const QrCodeGeneratorTool = () => {
  const [text, setText] = useState('');
  const [size, setSize] = useState(200);
  const [color, setColor] = useState('#000000');
  const [bgColor, setBgColor] = useState('#ffffff');
  const canvasRef = useRef(null);

  // Simple QR Code generation using Google Charts API
  const qrUrl = text 
    ? `https://api.qrserver.com/v1/create-qr-code/?size=${size}x${size}&data=${encodeURIComponent(text)}&color=${color.slice(1)}&bgcolor=${bgColor.slice(1)}`
    : '';

  const copyUrl = () => {
    navigator.clipboard.writeText(qrUrl);
    toast.success('QR code URL copied!');
  };

  const downloadQr = async () => {
    if (!qrUrl) return;
    
    try {
      const response = await fetch(qrUrl);
      const blob = await response.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'qrcode.png';
      a.click();
      URL.revokeObjectURL(url);
      toast.success('QR code downloaded!');
    } catch (e) {
      toast.error('Failed to download QR code');
    }
  };

  return (
    <div className="space-y-6" data-testid="qr-code-tool">
      <div className="grid md:grid-cols-2 gap-6">
        <div className="space-y-4">
          <div>
            <label className="text-sm text-muted-foreground mb-2 block">
              Content (URL, text, etc.)
            </label>
            <Input
              placeholder="https://example.com or any text"
              value={text}
              onChange={(e) => setText(e.target.value)}
            />
          </div>

          <div>
            <label className="text-sm text-muted-foreground mb-2 block">Size: {size}px</label>
            <input
              type="range"
              min="100"
              max="400"
              value={size}
              onChange={(e) => setSize(parseInt(e.target.value))}
              className="w-full"
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="text-sm text-muted-foreground mb-2 block">QR Color</label>
              <div className="flex gap-2">
                <input
                  type="color"
                  value={color}
                  onChange={(e) => setColor(e.target.value)}
                  className="w-10 h-10 rounded cursor-pointer"
                />
                <Input value={color} onChange={(e) => setColor(e.target.value)} />
              </div>
            </div>
            <div>
              <label className="text-sm text-muted-foreground mb-2 block">Background</label>
              <div className="flex gap-2">
                <input
                  type="color"
                  value={bgColor}
                  onChange={(e) => setBgColor(e.target.value)}
                  className="w-10 h-10 rounded cursor-pointer"
                />
                <Input value={bgColor} onChange={(e) => setBgColor(e.target.value)} />
              </div>
            </div>
          </div>
        </div>

        <div className="flex flex-col items-center justify-center">
          <div 
            className="p-4 rounded-xl border border-border bg-white flex items-center justify-center"
            style={{ minWidth: size + 32, minHeight: size + 32 }}
          >
            {qrUrl ? (
              <img src={qrUrl} alt="QR Code" style={{ width: size, height: size }} />
            ) : (
              <div className="text-muted-foreground text-center">
                <QrCode className="w-16 h-16 mx-auto mb-2 opacity-30" />
                <p className="text-sm">Enter content to generate QR code</p>
              </div>
            )}
          </div>
        </div>
      </div>

      <div className="flex gap-2 justify-center">
        <Button onClick={downloadQr} disabled={!text}>
          <Download className="w-4 h-4 mr-2" /> Download PNG
        </Button>
        <Button onClick={copyUrl} variant="outline" disabled={!text}>
          <Copy className="w-4 h-4 mr-2" /> Copy URL
        </Button>
        <Button onClick={() => setText('')} variant="outline">
          <RefreshCw className="w-4 h-4 mr-2" /> Clear
        </Button>
      </div>
    </div>
  );
};

export default QrCodeGeneratorTool;
